/**
 * Which pixels the fog blur of one cell reads and which it writes (ADR-100 decision 63).
 */

import { fogBlurRadius } from "./radius.ts";

export interface FogRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface FogBlurPlan {
  readonly radius: number;
  readonly margin: number;
  readonly read: FogRect;
  readonly write: FogRect;
  readonly offset: { readonly x: number; readonly y: number };
}

const PASSES = 2;

/**
 * Throws unless a value is an integer.
 *
 * @param value Value to check.
 * @param label Name used in the error message.
 * @throws RangeError when the value is not an integer.
 */
function assertInteger(value: number, label: string): void {
  if (!Number.isInteger(value)) {
    throw new RangeError(`${label} must be an integer, received ${String(value)}`);
  }
}

/**
 * Plans the blur of one fog cell: the pixels to read from the ground picture and the pixels to write to the display.
 * The read rectangle is the cell grown by twice the radius (two passes, each reaching one radius), clamped to the map;
 * the write rectangle is the cell's own pixels clipped to the map. Pass the read rectangle to `blurRgba` and copy the
 * write rectangle, found at `offset` inside the result, onto the display.
 *
 * @param cell The cell's rectangle in picture pixels; it may overhang the map.
 * @param scale Pixels per tile of the picture, a positive finite number.
 * @param map The picture's size in pixels, integers of at least 1.
 * @returns The radius, the margin, the read and write rectangles, and the write rectangle's offset inside the read one.
 * @throws RangeError when a size is not a positive integer, a position is not an integer, the scale is invalid, or the cell lies wholly off the map.
 */
export function fogBlurPlan(cell: FogRect, scale: number, map: { readonly width: number; readonly height: number }): FogBlurPlan {
  assertInteger(cell.x, "cell.x");
  assertInteger(cell.y, "cell.y");
  for (const [label, value] of [["cell.width", cell.width], ["cell.height", cell.height], ["map.width", map.width], ["map.height", map.height]] as const) {
    assertInteger(value, label);
    if (value < 1) {
      throw new RangeError(`${label} must be at least 1, received ${String(value)}`);
    }
  }
  const radius = fogBlurRadius(scale);
  const margin = PASSES * radius;
  const left = Math.max(0, cell.x);
  const top = Math.max(0, cell.y);
  const right = Math.min(map.width, cell.x + cell.width);
  const bottom = Math.min(map.height, cell.y + cell.height);
  if (right <= left || bottom <= top) {
    throw new RangeError("the cell lies wholly off the map");
  }
  const readLeft = Math.max(0, cell.x - margin);
  const readTop = Math.max(0, cell.y - margin);
  const readRight = Math.min(map.width, cell.x + cell.width + margin);
  const readBottom = Math.min(map.height, cell.y + cell.height + margin);
  return {
    radius,
    margin,
    read: { x: readLeft, y: readTop, width: readRight - readLeft, height: readBottom - readTop },
    write: { x: left, y: top, width: right - left, height: bottom - top },
    offset: { x: left - readLeft, y: top - readTop },
  };
}
